import React from 'react';
import AdminShell from '../../../components/admin/AdminShell';
import { KpiCard } from '../../../components/admin/KpiCard';
import { useTemplates, useCampaigns } from '../../../hooks/useMarketingQueries';
import { Mail, Send, TrendingUp, MessageSquare, AlertCircle, Plus, Activity } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

export default function MarketingDashboardPage() {
  const navigate = useNavigate();
  
  // Fetch templates and campaigns using React Query
  const { data: templates = [], isLoading: templatesLoading } = useTemplates();
  const { data: campaigns = [], isLoading: campaignsLoading } = useCampaigns();
  
  const loading = templatesLoading || campaignsLoading;
  
  // Compute stats client-side
  const stats = React.useMemo(() => {
    return {
      total_templates: templates.length,
      sms_templates: templates.filter(t => t.channel === 'sms').length,
      whatsapp_templates: templates.filter(t => t.channel === 'whatsapp').length,
      campaigns_running: campaigns.filter(c => c.status === 'running').length,
      campaigns_total: campaigns.length,
      sent_today: campaigns.reduce((sum, c) => sum + (c.total_sent || 0), 0),
      sent_this_week: campaigns.reduce((sum, c) => sum + (c.total_sent || 0), 0),
      delivery_rate: (() => {
        const totalSent = campaigns.reduce((sum, c) => sum + (c.total_sent || 0), 0);
        const totalDelivered = campaigns.reduce((sum, c) => sum + (c.total_delivered || 0), 0);
        return totalSent > 0 ? Math.round((totalDelivered / totalSent) * 100) : 0;
      })(),
      failed_today: campaigns.reduce((sum, c) => sum + (c.total_failed || 0), 0),
    };
  }, [templates, campaigns]);
  
  const recentCampaigns = React.useMemo(() => {
    return campaigns.slice(0, 8);
  }, [campaigns]);

  return (
    <AdminShell 
      title="Marketing" 
      actions={
        <Button 
          onClick={() => navigate('/admin/marketing/campaigns/new')}
          className="bg-[#D4A843] text-black hover:brightness-110 gap-2"
          data-testid="marketing-dashboard-new-campaign-button"
        >
          <Plus size={16} />
          New Campaign
        </Button>
      }
    >
      <div className="space-y-6">
        {/* KPI Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            title="Total Templates"
            value={loading ? '—' : stats.total_templates || 0}
            subtitle={`${stats.sms_templates || 0} SMS, ${stats.whatsapp_templates || 0} WhatsApp`}
            icon={Mail}
            loading={loading}
            onClick={() => navigate('/admin/marketing/templates')}
            className="cursor-pointer hover:scale-[1.02] transition-transform"
            data-testid="dashboard-total-templates-card"
          />
          <KpiCard
            title="Campaigns Running"
            value={loading ? '—' : stats.campaigns_running || 0}
            subtitle={`${stats.campaigns_total || 0} total`}
            icon={Send}
            loading={loading}
            onClick={() => navigate('/admin/marketing/campaigns')}
            className="cursor-pointer hover:scale-[1.02] transition-transform"
            data-testid="dashboard-campaigns-running-card"
          />
          <KpiCard
            title="Sent Today"
            value={loading ? '—' : stats.sent_today || 0}
            subtitle={`${stats.sent_this_week || 0} this week`}
            icon={MessageSquare}
            loading={loading}
            data-testid="dashboard-sent-today-card"
          />
          <KpiCard
            title="Delivery Rate"
            value={loading ? '—' : `${stats.delivery_rate || 0}%`}
            subtitle={`${stats.failed_today || 0} failed today`}
            icon={TrendingUp}
            loading={loading}
            data-testid="dashboard-delivery-rate-card"
          />
        </div>

        {/* Quick links to Marketing tools */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <button
            type="button"
            onClick={() => navigate('/admin/marketing/sms-health')}
            className="surface-card card-highlight rounded-xl border border-[#2A2A2A] bg-[#0E0E0E] hover:border-[#D4A843]/40 transition-colors px-5 py-4 text-left flex items-center gap-3"
            data-testid="marketing-link-sms-health"
          >
            <span className="h-10 w-10 rounded-lg bg-[rgba(212,168,67,0.12)] border border-[rgba(212,168,67,0.30)] flex items-center justify-center text-[#D4A843]">
              <Activity size={18} />
            </span>
            <span className="flex-1">
              <span className="block text-sm font-medium text-[#F2F2F2]">SMS Health</span>
              <span className="block text-xs text-[#B9B9B9]">MSG91 DLT config · test OTP · recent sends</span>
            </span>
          </button>
        </div>

        {/* Chart - Only show if we have data */}
        {false && ( // Disabled chart until we have historical data endpoint
          <div className="surface-card card-highlight rounded-xl p-5">
            <h2 className="font-['EB_Garamond'] text-lg text-[#F2F2F2] mb-4">Messages Sent (Last 7 Days)</h2>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="sentGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#D4A843" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#D4A843" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" stroke="#B9B9B9" style={{ fontSize: '12px' }} />
                <YAxis stroke="#B9B9B9" style={{ fontSize: '12px' }} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#111111', border: '1px solid #2A2A2A', borderRadius: '8px' }}
                  labelStyle={{ color: '#F2F2F2' }}
                  itemStyle={{ color: '#D4A843' }}
                />
                <Area type="monotone" dataKey="sent" stroke="#D4A843" fillOpacity={1} fill="url(#sentGradient)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Recent Campaigns */}
        <div className="surface-card card-highlight rounded-xl">
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#2A2A2A]">
            <h2 className="font-['EB_Garamond'] text-lg text-[#F2F2F2]">Recent Campaigns</h2>
            <Button
              variant="outline"
              size="sm"
              className="text-xs bg-transparent border-[#2A2A2A] text-[#B9B9B9] hover:border-[#D4A843]/40"
              onClick={() => navigate('/admin/marketing/campaigns')}
            >
              View all
            </Button>
          </div>
          <div className="divide-y divide-[#1E1E1E]">
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="px-5 py-3 flex gap-3 items-center">
                  <div className="h-8 w-8 bg-[#202020] rounded-full animate-pulse" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 w-32 bg-[#202020] rounded animate-pulse" />
                    <div className="h-2 w-20 bg-[#1A1A1A] rounded animate-pulse" />
                  </div>
                </div>
              ))
            ) : recentCampaigns.length === 0 ? (
              <div className="px-5 py-12 text-center">
                <AlertCircle size={40} className="mx-auto mb-3 text-[#6F6F6F]" />
                <p className="text-[#B9B9B9] text-sm mb-4">No campaigns yet</p>
                <Button
                  onClick={() => navigate('/admin/marketing/campaigns/new')}
                  className="bg-[#D4A843] text-black hover:brightness-110"
                >Create your first campaign</Button>
              </div>
            ) : (
              recentCampaigns.slice(0, 8).map(campaign => {
                const statusColors = {
                  draft: 'bg-[rgba(185,185,185,0.12)] border-[rgba(185,185,185,0.22)] text-[#B9B9B9]',
                  scheduled: 'bg-[rgba(212,168,67,0.12)] border-[rgba(212,168,67,0.22)] text-[#F1E2B8]',
                  running: 'bg-[rgba(61,220,151,0.12)] border-[rgba(61,220,151,0.22)] text-[#BFF3D2]',
                  paused: 'bg-[rgba(241,200,75,0.12)] border-[rgba(241,200,75,0.22)] text-[#F1E2B8]',
                  completed: 'bg-[rgba(212,168,67,0.12)] border-[rgba(212,168,67,0.22)] text-[#F1E2B8]',
                  failed: 'bg-[rgba(255,77,77,0.12)] border-[rgba(255,77,77,0.22)] text-[#FFD7D7]'
                };
                return (
                  <div
                    key={campaign._id}
                    className="px-5 py-3 flex items-center gap-3 hover:bg-[#161616] cursor-pointer transition-colors"
                    onClick={() => navigate(`/admin/marketing/campaigns/${campaign._id}`)}
                    data-testid="recent-campaign-row"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#F2F2F2] font-medium truncate">{campaign.name}</p>
                      <p className="text-xs text-[#B9B9B9]">
                        {campaign.channel?.toUpperCase()} • {campaign.total_recipients || 0} recipients
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-xs text-[#B9B9B9]">Delivered</p>
                        <p className="text-sm text-[#F2F2F2] font-medium">{campaign.analytics?.delivered || 0}</p>
                      </div>
                      <span className={`inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs border ${statusColors[campaign.status] || statusColors.draft}`}>
                        <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
                        {campaign.status}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </AdminShell>
  );
}
